/**
 * Client-side calculation of the hash OnlyFans uses to deduplicate Vault uploads.
 *
 * OnlyFans hashes a file before uploading it and checks it against
 * `GET /vault/media/hash?h=<hash>&size=<size>` to see whether the file already
 * exists. The hash it submits is the AWS S3 multipart-upload ETag:
 *
 *   - Files that fit in a single 5 MiB part  -> MD5 hex of the whole file.
 *   - Larger, multipart files                -> MD5 each 5 MiB part, concatenate
 *     the raw 16-byte digests, MD5 that blob, and append "-<partCount>".
 *
 * To match OnlyFans exactly we must reproduce this precise algorithm (a plain
 * SHA/MD5 of the file would not match). The actual hashing runs in a Web Worker
 * (see md5Worker.ts) using a WebAssembly MD5, so it is both fast and off the
 * main thread even for multi-gigabyte files.
 */

// Default S3 part size used by the OnlyFans uploader.
export const VAULT_UPLOAD_CHUNK_SIZE = 5 * 1024 * 1024; // 5 MiB

interface HashWorkerMessage {
    result?: string;
    error?: string;
    progress?: number;
}

/**
 * Calculates the OnlyFans Vault dedup hash (S3 multipart ETag) for a file.
 * The heavy lifting runs in a WASM-backed Web Worker to keep the UI responsive.
 */
export const calculateVaultMediaHash = (file: File, chunkSize: number = VAULT_UPLOAD_CHUNK_SIZE): Promise<string> =>
    new Promise((resolve, reject) => {
        let worker: Worker;

        try {
            worker = new Worker(new URL('./md5Worker.ts', import.meta.url));
        } catch (err) {
            reject(err instanceof Error ? err : new Error('Could not start hash worker'));
            return;
        }

        worker.onmessage = ({ data }: MessageEvent<HashWorkerMessage>) => {
            if (data.error) {
                reject(new Error(data.error));
                worker.terminate();
            } else if (typeof data.result === 'string') {
                resolve(data.result);
                worker.terminate();
            }
            // `progress` messages are informational and don't settle the promise.
        };

        worker.onerror = event => {
            reject(new Error(event.message || 'Hash worker error'));
            worker.terminate();
        };

        worker.postMessage({ file, chunkSize });
    });
